import { Component, ElementRef, ViewChild, inject, effect } from '@angular/core';
import type { MenuItem } from 'primeng/api';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { StyleClassModule } from 'primeng/styleclass';
import { LayoutService } from '../service/layout.service';
import { Ripple } from 'primeng/ripple';
import { InputText } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { FormsModule } from '@angular/forms';
import { UserService, ApplicationConfigService } from '@cadriciel/core';
import { ToolbarItem } from '../service/layout.models';
import { AuthService } from '@shared/auth/auth.service';
import { ActiveRoleService } from '@shared/auth/active-role.service';

@Component({
    selector: '[app-topbar]',
    standalone: true,
    imports: [RouterModule, CommonModule, StyleClassModule, FormsModule, Ripple, InputText, ButtonModule, IconField, InputIcon],
    template: `
        <div class="layout-topbar">
            <a class="app-logo" [routerLink]="$any(configService).authenticatedHomePath">
                <div class="logo-wrapper">
                    <img [src]="layoutService.layoutConfig().logoUrl" [alt]="layoutService.layoutConfig().logoAlt || 'Logo'" class="app-logo-img" />
                    <span class="app-version" *ngIf="layoutService.layoutConfig().appVersion">v{{ layoutService.layoutConfig().appVersion }}</span>
                </div>
                <div class="app-title-wrapper">
                    <span class="app-name">{{ layoutService.layoutConfig().appName || 'Ceremabo' }}</span>
                </div>
            </a>

            <button #menubutton class="topbar-menubutton p-link" type="button" (click)="onMenuButtonClick()">
                <span></span>
            </button>

            <ul class="topbar-menu">
                <li *ngFor="let item of tabs; let i = index">
                    <a
                        [routerLink]="item.routerLink"
                        routerLinkActive="active-route"
                        [routerLinkActiveOptions]="item.routerLinkActiveOptions || { paths: 'exact', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored' }"
                        [fragment]="item.fragment"
                        [queryParamsHandling]="item.queryParamsHandling"
                        [preserveFragment]="item.preserveFragment!"
                        [skipLocationChange]="item.skipLocationChange!"
                        [replaceUrl]="item.replaceUrl!"
                        [state]="item.state"
                        [queryParams]="item.queryParams"
                    >
                        <span>{{ item.label }}</span>
                    </a>
                    <i class="pi pi-times" (click)="removeTab($event, item, i)"></i>
                </li>
            </ul>

            <div class="topbar-actions">
                <p-button *ngIf="layoutService.layoutConfig().showSettings !== false" icon="pi pi-palette" rounded (onClick)="layoutService.showConfigSidebar()"></p-button>
                <div *ngIf="layoutService.layoutConfig().showSearch !== false" class="topbar-search" [ngClass]="{ 'topbar-search-active': searchActive }">
                    <button pButton [rounded]="true" severity="secondary" type="button" icon="pi pi-search" (click)="activateSearch()"></button>

                    <div class="search-input-wrapper">
                        <p-icon-field>
                            <input #searchinput type="text" pInputText placeholder="Search" (blur)="deactivateSearch()" (keydown.escape)="deactivateSearch()" />
                            <p-inputicon class="pi pi-search" />
                        </p-icon-field>
                    </div>
                </div>

                <div class="topbar-profile">
                    <button class="topbar-profile-button" type="button" pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                        @if (layoutService.layoutState().userAvatarUrl) {
                            <img alt="avatar" [src]="layoutService.layoutState().userAvatarUrl" />
                        } @else {
                            <div class="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-contrast font-bold text-sm shrink-0 mr-2">
                                {{ userInitials }}
                            </div>
                        }

                        <span class="profile-details" *ngIf="currentUserSignal() as user">
                            <span class="profile-name">{{ user.prenom }} {{ user.nom }}</span>
                            <span class="profile-job">{{ activeRoleService.activeRoleLabel() }}</span>
                        </span>
                        <i class="pi pi-angle-down"></i>
                    </button>
                    <ul class="list-none p-4 m-0 rounded-border shadow hidden absolute bg-surface-0 dark:bg-surface-900 origin-top w-full sm:w-56 mt-2 right-0 top-auto">
                        <!-- Changement de rôle (visible uniquement si plusieurs rôles) -->
                        <ng-container *ngIf="availableRoles().length > 1">
                            <li class="px-2 pt-1 pb-2 text-xs font-semibold text-muted-color uppercase">Changer de rôle</li>
                            <li *ngFor="let role of availableRoles()">
                                <a pRipple
                                   class="flex p-2 rounded-border items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                                   (click)="activeRoleService.setActiveRole(role)">
                                    <i class="pi !mr-4" [ngClass]="role === activeRoleService.activeRole() ? 'pi-check-circle text-primary' : 'pi-circle'"></i>
                                    <span class="hidden sm:inline">{{ activeRoleService.roleLabel(role) }}</span>
                                </a>
                            </li>
                            <li class="my-2 border-t border-surface-200 dark:border-surface-700"></li>
                        </ng-container>

                        <!-- Liens administrateur d'organisme -->
                        <ng-container *ngIf="activeRoleService.isOrgAdminActive()">
                            <li>
                                <a pRipple href="/recette/" target="_blank" rel="noopener"
                                   class="flex p-2 rounded-border items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-clipboard !mr-4"></i>
                                    <span class="hidden sm:inline">Recette</span>
                                </a>
                            </li>
                            <li>
                                <a pRipple href="/presentation/" target="_blank" rel="noopener"
                                   class="flex p-2 rounded-border items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer">
                                    <i class="pi pi-desktop !mr-4"></i>
                                    <span class="hidden sm:inline">Présentation</span>
                                </a>
                            </li>
                            <li class="my-2 border-t border-surface-200 dark:border-surface-700"></li>
                        </ng-container>

                        <li *ngFor="let item of profileMenuItems">
                            <a pRipple
                               class="flex p-2 rounded-border items-center hover:bg-emphasis transition-colors duration-150 cursor-pointer"
                               [routerLink]="item.routerLink"
                               (click)="onToolbarItemClick(item)">
                                <i [class]="item.icon + ' !mr-4'"></i>
                                <span class="hidden sm:inline">{{ item.label }}</span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    `,
    host: {
        class: 'layout-topbar'
    }
})
export class AppTopbar {
    menu: MenuItem[] = [];

    @ViewChild('searchinput') searchInput!: ElementRef;

    @ViewChild('menubutton') menuButton!: ElementRef;

    searchActive: boolean = false;

    layoutService = inject(LayoutService);
    userService = inject(UserService);
    configService = inject(ApplicationConfigService);
    authService = inject(AuthService);
    activeRoleService = inject(ActiveRoleService);

    // Liste des rôles de l'utilisateur courant, pour le switch de rôle.
    availableRoles = this.activeRoleService.availableRoles;

    constructor() {
        effect(() => {
            console.log('AppTopbar: layoutConfig appName:', this.layoutService.layoutConfig().appName);
            console.log('AppTopbar: layoutConfig logoUrl:', this.layoutService.layoutConfig().logoUrl);
            console.log('AppTopbar: layoutConfig full:', this.layoutService.layoutConfig());
        });
    }

    // Expose the signal directly for the template
    currentUserSignal = this.userService.currentUser;

    get userInitials(): string {
        const user = this.currentUserSignal();
        if (!user) return 'CK';
        const fp = user.prenom ? user.prenom.charAt(0).toUpperCase() : '';
        const fn = user.nom ? user.nom.charAt(0).toUpperCase() : '';
        return `${fp}${fn}`;
    }

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    activateSearch() {
        this.searchActive = true;
        setTimeout(() => {
            this.searchInput.nativeElement.focus();
        }, 100);
    }

    deactivateSearch() {
        this.searchActive = false;
    }

    removeTab(event: any, item: MenuItem, index: number) {
        this.layoutService.onTabClose(item, index);
        event.preventDefault();
    }

    get layoutTheme(): string | undefined {
        return this.layoutService.layoutConfig().layoutTheme;
    }
    set layoutTheme(value: string) {
        this.layoutService.layoutConfig.update((state) => ({ ...state, layoutTheme: value }));
    }

    get logo(): string {
        const basePath = 'layout/logo-';
        const logo = this.layoutService.isDarkTheme() || this.layoutService.layoutConfig().layoutTheme === 'primaryColor' ? 'light.svg' : 'dark.svg';
        return basePath + logo;
    }

    get tabs(): MenuItem[] {
        return this.layoutService.tabs;
    }

    get profileMenuItems(): ToolbarItem[] {
        const rightItems = this.layoutService.layoutConfig().toolbarConfig?.rightItems || [];
        // Get sub-items from the first rightItem (profile dropdown)
        return rightItems[0]?.items || [];
    }

    onToolbarItemClick(item: ToolbarItem) {
        if (item.command) {
            item.command();
        } else if (item.label === 'Logout') {
            this.authService.logout();
        }
    }

    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }

    toggleConfigSidebar() {
        let layoutState = this.layoutService.layoutState();

        if (this.layoutService.isSidebarActive()) {
            layoutState.overlayMenuActive = false;
            layoutState.overlaySubmenuActive = false;
            layoutState.staticMenuMobileActive = false;
            layoutState.menuHoverActive = false;
            layoutState.configSidebarVisible = false;
        }
        layoutState.configSidebarVisible = !layoutState.configSidebarVisible;
        this.layoutService.layoutState.set(layoutState);
    }
}

